import React from 'react';
import { StyledTextField } from '../productInformation';
import { Autocomplete, Checkbox, FormHelperText } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useTranslation } from 'react-i18next';
import { useProductFieldError } from '../validation';
import {
  parseTargetAudience,
  TARGET_AUDIENCE_OPTION_IDS,
  TargetAudienceOptionId,
  toTargetAudience,
} from 'src/Lca/targetAudience';
import { ComplianceField } from 'src/Lca/compliance';

interface Props {
  values: {
    regulatory_frameworks: string;
    goals: {
      intended_applications: string;
      study_reasons: string;
      target_audience: string;
      comissioner_name: string;
    };
  };
  setValues: Function;
}

export default function GoalsBasic({ setValues, values }: Props) {
  const { t, i18n } = useTranslation('lca', { keyPrefix: 'goals' });
  const parsedTargetAudience = parseTargetAudience(
    values.goals.target_audience
  );
  const intendedApplicationsError = useProductFieldError(
    'intended_applications',
    values.goals.intended_applications
  );
  const studyReasonsError = useProductFieldError(
    'study_reasons',
    values.goals.study_reasons
  );
  const targetAudienceError = useProductFieldError(
    'target_audience',
    values.goals.target_audience
  );
  const commissionerError = useProductFieldError(
    'comissioner_name',
    values.goals.comissioner_name
  );
  const targetAudienceHelperText =
    targetAudienceError || t('targetAudienceMultiSelectHint');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    name:
      | 'intended_applications'
      | 'comissioner_name'
      | 'study_reasons'
      | 'target_audience'
  ) => {
    setValues({
      ...values,
      goals: { ...values.goals, [name]: e.target.value },
    });
  };

  const getTargetAudienceOptionLabel = (optionId: TargetAudienceOptionId) =>
    t(`targetAudienceOptions.${optionId}`);

  const handleTargetAudienceSelect = (selectedAudience: string[] | null) => {
    setValues({
      ...values,
      goals: {
        ...values.goals,
        target_audience: toTargetAudience(
          {
            audience: (selectedAudience || []) as TargetAudienceOptionId[],
            details: parsedTargetAudience.details,
          },
          {
            language: i18n.resolvedLanguage || i18n.language,
          }
        ),
      },
    });
  };

  const handleTargetAudienceDetails = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({
      ...values,
      goals: {
        ...values.goals,
        target_audience: toTargetAudience(
          {
            audience: parsedTargetAudience.audience,
            details: e.target.value,
          },
          {
            language: i18n.resolvedLanguage || i18n.language,
          }
        ),
      },
    });
  };

  const targetAudienceOptions = TARGET_AUDIENCE_OPTION_IDS.map((optionId) => ({
    value: optionId,
    label: getTargetAudienceOptionLabel(optionId),
  }));
  const selectedTargetAudienceOptions = targetAudienceOptions.filter((option) =>
    parsedTargetAudience.audience.includes(option.value)
  );

  return (
    <div
      style={{
        display: 'flex',
        gap: '24px',
        flexDirection: 'column',
      }}
    >
      <ComplianceField
        excelRow={105}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('specyficApplicationQuestion')}
          name="intended_applications"
          value={values.goals.intended_applications}
          onChange={(e) => handleChange(e, 'intended_applications')}
          multiline
          rows={3}
          error={!!intendedApplicationsError}
          helperText={intendedApplicationsError}
        />
      </ComplianceField>

      <ComplianceField
        excelRow={106}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('studyReasonsQuestion')}
          name="study_reasons"
          value={values.goals.study_reasons}
          onChange={(e) => handleChange(e, 'study_reasons')}
          multiline
          rows={3}
          error={!!studyReasonsError}
          helperText={studyReasonsError}
        />
      </ComplianceField>

      <ComplianceField
        excelRow={107}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <div>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={targetAudienceOptions}
            value={selectedTargetAudienceOptions}
            onChange={(_, selectedOptions) =>
              handleTargetAudienceSelect(
                selectedOptions.map((option) => option.value)
              )
            }
            isOptionEqualToValue={(option, selectedValue) =>
              option.value === selectedValue.value
            }
            getOptionLabel={(option) => option.label}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={selected}
                  sx={{ mr: 1 }}
                />
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                fullWidth
                label={t('targetAudienceDropdownLabel')}
                placeholder={
                  selectedTargetAudienceOptions.length
                    ? ''
                    : t('selectMultipleOptions')
                }
                error={!!targetAudienceError}
              />
            )}
          />
          <FormHelperText error={!!targetAudienceError}>
            {targetAudienceHelperText}
          </FormHelperText>
        </div>
      </ComplianceField>

      <ComplianceField
        excelRow={108}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('targetAudienceTextQuestion')}
          name="target_audience_details"
          value={parsedTargetAudience.details}
          onChange={handleTargetAudienceDetails}
          error={!!targetAudienceError}
          helperText={targetAudienceError}
        />
      </ComplianceField>

      <ComplianceField
        excelRow={109}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('commisionerOfTheStudyName')}
          name="comissioner_name"
          value={values.goals.comissioner_name}
          onChange={(e) => handleChange(e, 'comissioner_name')}
          error={!!commissionerError}
          helperText={commissionerError}
        />
      </ComplianceField>
    </div>
  );
}
